import {
  libelleLienParties, libelleOriginePropriete, libellePieceIdentite,
  libelleRegimeMatrimonial, libelleSituationMatrimoniale,
} from './libelles.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LIRE UN FORMULAIRE D'ÉTAT SANS CONNAÎTRE LA BASE.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Demande du fondateur, 24/09/2026 : « il faut une vraie page de récapitulatif
 * avec un bouton ou icône pour retourner à cette étape et modifier ».
 *
 * ⚠️⚠️ CE QUE CE MODULE REMPLACE. La fiche d'une demande rendait le formulaire
 * ainsi : `<pre>{JSON.stringify(d.formulaire, null, 2)}</pre>`. L'agent chargé
 * de CERTIFIER l'acte lisait `"communeOuSousPrefecture": "matoto"` et
 * `"pieceType": "passeport"` — des clés de base de données, dans l'ordre du
 * sérialiseur, sur un poste d'État. C'est le §68 : « l'utilisateur ne doit pas
 * avoir à comprendre la base de données pour effectuer son travail ».
 *
 * ⚠️⚠️ UN SEUL RENDU POUR DEUX ÉCRANS, ET C'EST TOUT L'INTÉRÊT. Le
 * récapitulatif AVANT dépôt lit le corps que `construire()` va envoyer ; la
 * fiche APRÈS dépôt lit `fon.demande.formulaire`, qui est ce même corps tel
 * qu'il a été reçu. Deux rendus divergeraient au premier champ ajouté — et le
 * jour où ils divergent, l'agent relit à l'écran autre chose que ce qu'il
 * dépose. Une seule fonction, donc, et la relecture vaut preuve.
 *
 * ⚠️ LES CHAMPS INCONNUS SONT AFFICHÉS, PAS TUS. Un formulaire qui porterait
 * une clé absente de ce catalogue la rendrait telle quelle, signalée. Les
 * sauter ferait relire — et certifier — un acte amputé sans que rien ne le
 * dise : c'est exactement le défaut que l'on corrige.
 */

/** Le nom du BLOC, dans l'ordre où l'acte le raconte. */
export const BLOCS: ReadonlyArray<readonly [string, string]> = [
  ['demandeur', 'Le demandeur'],
  ['immeuble', "Situation de l'immeuble"],
  ['personneInscrite', 'Personne inscrite au registre'],
  ['donateur', 'Celui qui donne — « donateur » à l’acte'],
  ['donataire', 'Celui qui reçoit — « donataire » à l’acte'],
  ['vendeur', 'Le vendeur'],
  ['acquereur', "L'acquéreur"],
  ['creancier', 'Le créancier'],
  ['debiteur', 'Le débiteur'],
  ['constituant', 'Le constituant'],
  ['premierCopermutant', 'Le premier copermutant'],
  ['secondCopermutant', 'Le second copermutant'],
  ['titulaireInscription', "Le titulaire de l'inscription radiée"],
  ['proprietaireFonds', 'Le propriétaire du fonds apuré'],
  ['defunt', 'Le défunt'],
  ['ayantsDroit', 'Les ayants droit déclarés'],
  ['heritiers', 'Les ayants droit déclarés'],
  ['origine', "D'où le cédant tient l'immeuble"],
  ['origineDetention', 'Origine de la détention'],
  ['secondImmeuble', 'Le second immeuble'],
  ['surete', 'La sûreté'],
  ['creance', 'La créance garantie'],
  ['temoins', 'Témoins'],
  ['piecesJointes', 'Pièces jointes'],
];

/** Le nom du CHAMP. Les clés se répètent d'un bloc à l'autre : un seul nom. */
export const CHAMPS: Record<string, string> = {
  qualite: 'Qualité', agitPour: 'Agit', estPartie: 'Qualité aux actes',
  mandantNom: 'Mandant', mandatReference: 'Référence du mandat',
  personne: 'Identité',
  nom: 'Nom', prenoms: 'Prénoms', denomination: 'Dénomination',
  formeJuridique: 'Forme juridique', rccm: 'RCCM', nif: 'NIF',
  representantNom: 'Représentant — nom', representantPrenoms: 'Représentant — prénoms',
  representantQualite: 'Qualité du représentant',
  dateNaissance: 'Date de naissance', lieuNaissance: 'Lieu de naissance',
  nationalite: 'Nationalité', profession: 'Profession',
  pere: 'Père', mere: 'Mère', pereNom: 'Nom du père', mereNom: 'Nom de la mère',
  decede: 'Décédé(e)',
  situationMatrimoniale: 'Situation matrimoniale', regimeMatrimonial: 'Régime matrimonial',
  conjointNom: 'Conjoint', conjointConsent: 'Consentement du conjoint',
  pieceType: "Pièce d'identité", pieceNumero: 'Numéro de la pièce',
  pieceDelivreeLe: 'Délivrée le', nin: 'Identifiant national (NIN)',
  telephone: 'Téléphone', courriel: 'Courriel', adresse: 'Adresse',
  siegeAdresse: 'Siège',
  region: 'Région', prefecture: 'Préfecture',
  communeOuSousPrefecture: 'Commune / Sous-préfecture',
  quartierOuDistrict: 'Quartier / District', precision: 'Repère de terrain',
  identifiantParcelle: 'Identifiant de parcelle', referenceCadastrale: 'Référence cadastrale',
  lieuDit: 'Lieu-dit', lotCopropriete: 'Lot de copropriété',
  superficieDeclareeM2: 'Superficie déclarée (m²)',
  mode: 'Mode de détention', detail: "Détail de l'origine", detientDepuis: 'Détient depuis',
  acteNature: "Nature de l'acte d'origine", acteNumero: "Numéro de l'acte",
  acteDate: "Date de l'acte", acteAutorite: "Autorité qui l'a reçu",
  precedentProprietaire: 'Précédent propriétaire', referenceInscription: "Référence d'inscription",
  lienEntreParties: 'Lien entre les parties', natureDroitDonne: 'Nature du droit donné',
  quotiteNum: 'Part — numérateur', quotiteDen: 'Part — dénominateur',
  chargesEtConditions: 'Charges et conditions',
  acceptationDonataire: 'Acceptation du bénéficiaire',
  declarationSansContrepartie: 'Déclaration de gratuité',
  engagementConfidentialite: 'Engagement de confidentialité',
  lienAvecLesParties: 'Lien avec les parties',
  objet: 'Renseignement demandé', motif: 'Motif de la demande',
  seulTitulaire: 'Seule titulaire', avecAutres: "Titulaire avec d'autres",
  qualiteSuccessorale: 'Qualité', lien: 'Lien', vocationNum: 'Part — numérateur',
  vocationDen: 'Part — dénominateur', dateDeces: 'Date du décès', lieuDeces: 'Lieu du décès',
  sourceDeces: 'Le décès est établi par', sourceDevolution: 'La dévolution est établie par',
  referenceActeDeces: "Référence de l'acte de décès",
  referenceDevolution: 'Référence du titre de dévolution',
  prix: 'Prix', devise: 'Devise', modalitePaiement: 'Modalité de paiement',
  /**
   * ⚠️ LE FAIT, PAS L'IMAGE : le portrait vit au coffre, chiffré. Ce booléen
   * permet à l'acte de distinguer « aucune photo n'a été prise » de « la photo
   * existe et je ne sais pas la retrouver ».
   */
  portraitPresent: 'Photographie',
  portraitDocumentId: 'Photographie',
};

/**
 * La VALEUR, quand c'est un code. On réemploie les dictionnaires de
 * `libelles.ts` — en écrire d'autres ici ferait une cinquième copie du
 * vocabulaire des pièces d'identité, celle qui diverge.
 */
const PAR_CHAMP: Record<string, (v: unknown) => string> = {
  pieceType: libellePieceIdentite,
  lienEntreParties: libelleLienParties,
  situationMatrimoniale: libelleSituationMatrimoniale,
  regimeMatrimonial: libelleRegimeMatrimonial,
  mode: libelleOriginePropriete,
  origineDetention: libelleOriginePropriete,
};

const MOTS: Record<string, string> = {
  personne_physique: 'Personne physique', personne_morale: 'Personne morale',
  soi_meme: 'Pour son propre compte', tiers: "Pour le compte d'un tiers",
  cedant: 'Partie qui cède', beneficiaire: 'Partie qui reçoit',
  propriete: 'Pleine propriété', nue_propriete: 'Nue-propriété — usufruit réservé',
  usufruit: 'Usufruit seul',
  releve_biens: 'Relevé des biens', situation_juridique: 'Situation juridique',
  plan_parcellaire: 'Plan parcellaire', charges_et_servitudes: 'Charges et servitudes',
};

export const libelleChamp = (cle: string): string => CHAMPS[cle] ?? cle;
export const libelleBloc = (cle: string): string =>
  BLOCS.find(([c]) => c === cle)?.[1] ?? CHAMPS[cle] ?? cle;

/**
 * ⚠️ « non renseigné » N'EST PAS UNE VALEUR : c'est une ABSENCE, et l'écran
 * doit la peindre autrement qu'un texte. On rend `null` pour que l'appelant
 * choisisse — une chaîne « — » se trierait, se copierait, et finirait sur un
 * acte.
 */
export function libelleValeur(cle: string, v: unknown): string | null {
  if (v === null || v === undefined || v === '') return null;
  /* ⚠️ « Oui » ne dit rien d'utile sur une photographie : ce qui compte est
     QUAND et OÙ elle a été prise. Le guichet est la réponse. */
  if (cle === 'portraitPresent') return v === true ? 'Prise au guichet' : null;
  if (typeof v === 'boolean') return v ? 'Oui' : 'Non';
  const par = PAR_CHAMP[cle];
  if (par) { const t = par(v); if (t) return t; }
  if (typeof v === 'string') return MOTS[v] ?? v;
  if (typeof v === 'number') return String(v);
  return null;
}
